import { type FunctionComponent } from 'react'
import { type SelectChangeEvent } from '@mui/material'
import { useLanguage } from '@/context/LanguageContext'
import useIntl from '@/utils/intl'
import cn from 'classnames'
import LocationSelect from '../LocationSelect'
import { type City, type SelectItem, type TravelPoint } from '@/lib/network-coverage-v2/types'
import { type TravelPointFilter } from '@/services/cms-api/network-coverage/types'
import { localeName, toOptional } from '@/lib/network-coverage-v2/helpers'

interface TravelPointsTabProps {
  cities: City[]
  selectedCity: City | null
  travelPoints: TravelPoint[]
  selectedTravelPoint: TravelPoint | null
  cityItems: SelectItem[]
  iconColor: string
  onTravelPointSelect: (item: TravelPoint) => void
  onCityChange: (city: City | null, query: TravelPointFilter) => void
}

const TravelPointsTab: FunctionComponent<TravelPointsTabProps> = ({
  cities,
  selectedCity,
  travelPoints,
  selectedTravelPoint,
  cityItems,
  iconColor,
  onTravelPointSelect,
  onCityChange
}) => {
  const { translate } = useIntl()
  const { locale } = useLanguage()

  const handleCitySelect = (event: SelectChangeEvent<string>): void => {
    const city = cities.find((c) => c.zipcode === event.target.value) ?? null
    onCityChange(city, { cityzipcode: toOptional(event.target.value) })
  }

  return (
    <div className='overflow-auto h-full lg:flex flex-col visible'>
      <div className='p-6 border-b border-secondaryMidStroke'>
        <LocationSelect
          label={translate('city')}
          value={selectedCity?.zipcode ?? ''}
          items={[{ key: '', value: '', label: translate('all') }, ...cityItems]}
          onChange={handleCitySelect}
          iconColor={iconColor}
        />
      </div>
      <div id='tplist' className='overflow-auto h-full flex flex-col'>
        {travelPoints?.map((item) => (
          <div
            key={item.id}
            className={cn(
              'p-4 cursor-pointer',
              item.id === selectedTravelPoint?.id
                ? 'bg-surfaceLow border-l-4 border-[#EF4444]'
                : 'border-secondaryMidStroke'
            )}
            onClick={() => { onTravelPointSelect(item) }}
          >
            <span className='text-textHigh lg:text-lg font-bold'>
              {localeName(item, locale)}
            </span>
            <div className='flex justify-between mt-3'>
              <p className='text-textMid text-sm font-medium'>{translate('networktype')}:</p>
              <span className='justify-end text-sm font-bold text-textHigh'>
                {item.twoG ? '2G   ' : ''}
                {item.threeG ? '3G   ' : ''}
                {item.fourG ? '4G   ' : ''}
                {item.fiveG ? '5G' : ''}
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default TravelPointsTab
